import { Input } from '@farol-ds/react'
import classNames from 'classnames'
import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import styles from './pin-input.module.scss'

export interface PinInputProps {
  length?: number
  error?: boolean
  disabled?: boolean
  autoFocus?: boolean
  /** Called whenever the value changes (partial included). */
  onChange?: (value: string) => void
  /** Called once all `length` digits are filled — drives auto-submit. */
  onComplete?: (value: string) => void
  ariaLabel?: string
}

const isDigit = (value: string) => /^\d$/.test(value)

export function PinInput({
  length = 6,
  error = false,
  disabled = false,
  autoFocus = true,
  onChange,
  onComplete,
  ariaLabel = 'Código de verificação',
}: PinInputProps) {
  const [values, setValues] = useState<string[]>(() => Array(length).fill(''))
  const inputsRef = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (autoFocus) {
      inputsRef.current[0]?.focus()
    }
  }, [autoFocus])

  const emit = useCallback(
    (next: string[]) => {
      const joined = next.join('')
      onChange?.(joined)
      if (joined.length === length && next.every(Boolean)) {
        onComplete?.(joined)
      }
    },
    [length, onChange, onComplete]
  )

  const handleChange = useCallback(
    (index: number, raw: string) => {
      const digit = raw.slice(-1)
      if (raw !== '' && !isDigit(digit)) return

      const next = [...values]
      next[index] = digit
      setValues(next)
      // Side effects stay out of the state updater (which must be pure and may
      // run twice under StrictMode, double-firing onComplete).
      emit(next)

      if (digit && index < length - 1) {
        inputsRef.current[index + 1]?.focus()
      }
    },
    [emit, length, values]
  )

  const handleKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        if (!values[index] && index > 0) {
          e.preventDefault()
          inputsRef.current[index - 1]?.focus()
        }
        return
      }
      if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault()
        inputsRef.current[index - 1]?.focus()
      }
      if (e.key === 'ArrowRight' && index < length - 1) {
        e.preventDefault()
        inputsRef.current[index + 1]?.focus()
      }
    },
    [length, values]
  )

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLDivElement>) => {
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
      if (!pasted) return
      e.preventDefault()

      const next = Array.from({ length }, (_, i) => pasted[i] ?? '')
      setValues(next)
      // Side effects stay out of the state updater (see handleChange).
      emit(next)

      const focusIndex = Math.min(pasted.length, length - 1)
      inputsRef.current[focusIndex]?.focus()
    },
    [emit, length]
  )

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={styles.root} onPaste={handlePaste} role="group" aria-label={ariaLabel}>
      {values.map((value, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} className={styles.field}>
          <Input
            ref={(el: HTMLInputElement | null) => {
              inputsRef.current[index] = el
            }}
            className={classNames(styles.fieldInput, { [styles.fieldInputError]: error })}
            value={value}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(index, e.target.value)}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
            disabled={disabled}
            error={error}
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            aria-label={`Dígito ${index + 1}`}
          />
        </div>
      ))}
    </div>
  )
}
